import { useMemo } from 'react'

import { useRequest } from 'ahooks'
import { isNil } from 'lodash'

import { PriceIndexEntity } from '@eastcoal/api/content/interface'
import { queryPriceIndexListApi } from '@eastcoal/api/content/priceIndexMall/queryPriceIndexList'

import { Chart } from '@eastcoal/common/components/Chart'
import { isEmpty, pick } from '@eastcoal/common/utils/object'

import { usePriceIndexChartStore } from '../../Store/hooks/useStore'

import type { PriceIndexChartDiagramProps } from './interface'

const CacheMap = new Map<string, PriceIndexEntity[] | undefined>()

export const PriceIndexChartDiagram = ({
  className,
  style,
  height,
  width
}: PriceIndexChartDiagramProps) => {
  const { chartType, priceIndexType, currentKind, dateRange } =
    usePriceIndexChartStore(state =>
      pick(state, ['chartType', 'priceIndexType', 'currentKind', 'dateRange'])
    )

  const { data } = useRequest(
    () =>
      queryPriceIndexListApi(
        {
          priceIndexType,
          kindCode: currentKind?.code ?? '',
          startDate: dateRange?.[0],
          endDate: dateRange?.[1]
        },
        { silence: true }
      ),
    {
      manual: false,
      cacheKey: `PriceIndexChartDiagram-${priceIndexType}`,
      staleTime: Infinity,
      ready: !(isNil(priceIndexType) || isNil(currentKind)),
      refreshDeps: [priceIndexType, currentKind, dateRange],
      getCache(params) {
        const cacheKey = `${currentKind?.id}`
        if (currentKind && isEmpty(dateRange) && CacheMap.has(cacheKey)) {
          return {
            data: CacheMap.get(cacheKey),
            params,
            time: -1
          }
        }
        return undefined
      },
      setCache(data) {
        if (currentKind && isEmpty(dateRange)) {
          const cacheKey = `${currentKind.id}`
          CacheMap.set(cacheKey, data.data)
        }
      }
    }
  )

  const chartData = useMemo(() => {
    const filteredData = data?.filter(
      item => !(isNil(item.indexTime) || isNil(item.price))
    )
    return filteredData?.length
      ? {
          labels: filteredData.map(({ indexTime }) => indexTime),
          datasets: [
            {
              stepped: false,
              borderColor: '#d14a61',
              backgroundColor: '#d14a61',
              label: currentKind?.name,
              data: filteredData.map(item => item.price ?? 0)
            }
          ]
        }
      : {
          labels: [],
          datasets: []
        }
  }, [currentKind?.name, data])

  return (
    <div className={className} style={style}>
      <Chart
        data={chartData}
        height={height}
        refreshDeps={[chartType, chartData]}
        type={chartType}
        width={width}
        options={{
          responsive: true,
          interaction: {
            intersect: false,
            axis: 'x'
          },
          scales: {
            // y: {
            //   title: {
            //     display: true,
            //     text: '元/吨',
            //     align: 'end',
            //     padding: {
            //       x: -10
            //     }
            //   }
            // }
          },
          plugins: {
            subtitle: {
              display: true,
              text: '元/吨',
              position: 'top',
              align: 'start',
              padding: {
                top: 0,
                bottom: 10
              }
            },
            legend: {
              display: false
            }
          }
        }}
      />
    </div>
  )
}
